/**
 * Keyboard-shortcut helpers, shared by every surface that either advertises a
 * shortcut or listens for one.
 *
 * Extracted in VRG-248 rather than copied a second time: `SidebarSearchTrigger`
 * already carried its own platform sniff + "⌘K" formatting for the search row,
 * and the sidebar display-mode toggle needs the identical logic for ⌘B. Two
 * hand-rolls of the same thing is the point at which it becomes a shared module
 * (working-conventions §36) — otherwise the two drift the first time the
 * detection has to change.
 *
 * The platform is read once at module scope: it cannot change mid-session, and
 * this keeps the sniff out of render.
 */

/**
 * `navigator.platform` is deprecated but remains the only synchronously-readable
 * signal that works in every browser (`navigator.userAgentData` is Chromium-only
 * and its `platform` field is not exposed to non-secure contexts). Falls back to
 * the UA string so a browser that has already emptied `platform` isn't silently
 * treated as Windows.
 */
export const IS_APPLE_PLATFORM =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '')

/*
 * `formatShortcut(key)`, `ariaKeyShortcut(key)` and `hasPrimaryModifier(event)`
 * lived here and were REMOVED when shortcuts became user-rebindable. Each assumed
 * a binding was "the primary modifier plus one key", which stopped being true the
 * moment a user could record ⇧⌘K, and neither the label nor the matcher could be
 * widened without changing every signature.
 *
 * Their chord-aware replacements are `formatBinding`, `ariaBinding` and
 * `eventMatchesBinding` in `shortcutBindings.ts`, which produce identical output
 * for the single-modifier case — so no call site changed meaning when it moved.
 * Deleted rather than deprecated: nothing called them afterwards, and a second
 * formatter kept around "just in case" is exactly how two surfaces drift.
 */

/**
 * True when the event target is somewhere the user is entering text, so a global
 * shortcut must stand down. ⌘B in particular is *bold* inside a rich-text editor
 * and a browser-level binding inside a plain field, so swallowing it there would
 * break the thing the user is actually doing.
 *
 * Deliberately covers every `<input>` rather than only text-typed ones: the cost
 * of not toggling the sidebar while a checkbox has focus is nil, and enumerating
 * "text-ish" input types is a list that goes stale.
 */
export const isTypingTarget = (target: EventTarget | null) => {
  const element = target as HTMLElement | null
  if (!element || typeof element.tagName !== 'string') return false
  if (element.isContentEditable) return true
  if (element.getAttribute?.('role') === 'textbox') return true
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
}
